import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Printer, Upload, Plus, Trash2, Loader2, CheckCircle, FileUp, Download } from "lucide-react";
import { apiClient } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";

const DEFAULT_SIGNATORIES = [
  { name: "", title: "", role: "internal", company: "" },
  { name: "", title: "", role: "counterparty", company: "" },
];

export function PrintSignDialog({ open, onOpenChange, contract, user, onComplete }) {
  const [step, setStep] = useState("configure"); // configure | upload
  const [signatories, setSignatories] = useState(DEFAULT_SIGNATORIES);
  const [includeWitness, setIncludeWitness] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [notes, setNotes] = useState("");
  const [printSignStatus, setPrintSignStatus] = useState(null);

  useEffect(() => {
    if (open && contract) {
      fetchStatus();
      // Pre-fill from contract data
      const party = contract.contracting_party || {};
      const cf = contract.custom_fields || {};
      setSignatories([
        { name: cf.contract_owner_name || user?.name || "", title: "", role: "internal", company: "" },
        { name: party.legal_name || "", title: "", role: "counterparty", company: party.legal_name || "" },
      ]);
    }
  }, [open, contract]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchStatus = async () => {
    try {
      const res = await apiClient.get(`/signatures/print-sign/${contract.id}/status`);
      setPrintSignStatus(res.data);
      if (res.data.status === "pdf_generated") setStep("upload");
      else if (res.data.status === "executed") setStep("upload");
      else setStep("configure");
    } catch { /* ignore */ }
  };

  const updateSignatory = (index, field, value) => {
    const updated = [...signatories];
    updated[index] = { ...updated[index], [field]: value };
    setSignatories(updated);
  };

  const addWitness = () => {
    setSignatories([...signatories, { name: "", title: "", role: "witness", company: "" }]);
    setIncludeWitness(true);
  };

  const removeSignatory = (index) => {
    setSignatories(signatories.filter((_, i) => i !== index));
  };

  const handleGeneratePdf = async () => {
    const validSignatories = signatories.filter(s => s.name.trim());
    if (validSignatories.length < 2) {
      toast.error("At least two named signatories are required");
      return;
    }
    setGenerating(true);
    try {
      const res = await apiClient.post(
        `/signatures/print-sign/${contract.id}/generate`,
        { signatories: validSignatories, include_witness: includeWitness },
        { responseType: "blob" }
      );
      // Trigger download
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${contract.title?.replace(/\s+/g, "_") || "contract"}_FOR_SIGNING.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success("Signing PDF downloaded. Print, sign, scan, and upload the signed copy.");
      setStep("upload");
      fetchStatus();
    } catch {
      toast.error("Failed to generate signing PDF");
    } finally {
      setGenerating(false);
    }
  };

  const handleUploadSigned = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ["application/pdf", "image/jpeg", "image/png", "image/tiff"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please upload a PDF, JPEG, PNG, or TIFF file");
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error("File too large. Maximum 20MB.");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("notes", notes);
      await apiClient.post(`/signatures/print-sign/${contract.id}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Signed copy uploaded. Contract is now fully executed!");
      onComplete?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to upload signed copy");
    } finally {
      setUploading(false);
    }
  };

  const isExecuted = printSignStatus?.status === "executed";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Printer size={20} className="text-cyan-600" />
            Print & Sign — Wet-Ink Workflow
          </DialogTitle>
          <DialogDescription>
            Generate a professional PDF with signature blocks, print it, sign with wet ink, then upload the scanned copy.
          </DialogDescription>
        </DialogHeader>

        {/* Status Banner */}
        {isExecuted && (
          <div className="flex items-center gap-3 p-3 bg-emerald-50 border border-emerald-200 rounded-sm" data-testid="print-sign-executed-banner">
            <CheckCircle size={20} className="text-emerald-600" />
            <div>
              <p className="text-sm font-medium text-emerald-800">Contract Executed via Print & Sign</p>
              <p className="text-xs text-emerald-600">
                Signed copy: {printSignStatus.signed_filename} — uploaded {new Date(printSignStatus.uploaded_at).toLocaleDateString()}
              </p>
            </div>
          </div>
        )}

        {/* Progress Steps */}
        {!isExecuted && (
          <div className="flex items-center gap-2 py-2" data-testid="print-sign-steps">
            <StepIndicator
              number={1}
              label="Configure & Download"
              active={step === "configure"}
              done={step === "upload"}
            />
            <div className="flex-1 h-px bg-slate-200" />
            <StepIndicator
              number={2}
              label="Upload Signed Copy"
              active={step === "upload"}
              done={isExecuted}
            />
          </div>
        )}

        {/* Step 1: Configure Signatories */}
        {step === "configure" && !isExecuted && (
          <div className="space-y-4 py-2" data-testid="print-sign-configure">
            <div className="space-y-3">
              <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Signatories</Label>
              {signatories.map((sig, idx) => (
                <div key={idx} className="border rounded-sm p-3 space-y-2 bg-slate-50/50" data-testid={`signatory-block-${idx}`}>
                  <div className="flex items-center justify-between">
                    <Badge variant="outline" className="text-xs capitalize">{sig.role}</Badge>
                    {sig.role === "witness" && (
                      <Button variant="ghost" size="sm" className="h-6 text-red-500" onClick={() => removeSignatory(idx)}>
                        <Trash2 size={12} />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Full Name *</Label>
                      <Input
                        value={sig.name}
                        onChange={(e) => updateSignatory(idx, "name", e.target.value)}
                        placeholder={sig.role === "witness" ? "Witness name" : "Full name"}
                        className="rounded-sm h-9"
                        data-testid={`signatory-name-${idx}`}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Title / Position</Label>
                      <Input
                        value={sig.title}
                        onChange={(e) => updateSignatory(idx, "title", e.target.value)}
                        placeholder="e.g., Director, CFO"
                        className="rounded-sm h-9"
                        data-testid={`signatory-title-${idx}`}
                      />
                    </div>
                  </div>
                  {sig.role !== "witness" && (
                    <div>
                      <Label className="text-xs">Company / Entity</Label>
                      <Input
                        value={sig.company}
                        onChange={(e) => updateSignatory(idx, "company", e.target.value)}
                        placeholder="Company name"
                        className="rounded-sm h-9"
                        data-testid={`signatory-company-${idx}`}
                      />
                    </div>
                  )}
                </div>
              ))}
              {!signatories.some(s => s.role === "witness") && (
                <Button variant="outline" size="sm" onClick={addWitness} className="gap-1.5 rounded-sm" data-testid="add-witness-btn">
                  <Plus size={14} /> Add Witness
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Step 2: Upload Signed Copy */}
        {step === "upload" && !isExecuted && (
          <div className="space-y-4 py-2" data-testid="print-sign-upload">
            {printSignStatus?.status === "pdf_generated" && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-sm text-sm text-blue-800" data-testid="pdf-generated-info">
                <p className="font-medium">PDF was generated on {new Date(printSignStatus.generated_at).toLocaleDateString()}</p>
                <p className="text-xs text-blue-600 mt-1">Print the document, get all parties to sign, then scan and upload below.</p>
              </div>
            )}

            <Button
              variant="outline"
              size="sm"
              className="gap-2 rounded-sm"
              onClick={() => { setStep("configure"); }}
              data-testid="regenerate-pdf-btn"
            >
              <Download size={14} /> Re-generate / Download PDF Again
            </Button>

            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g., Signed at London office on 15th Feb 2026. Original retained by Legal."
                rows={2}
                className="rounded-sm"
                data-testid="upload-notes-input"
              />
            </div>

            <div className="border-2 border-dashed border-slate-300 rounded-sm p-8 text-center hover:border-cyan-400 transition-colors">
              <FileUp size={32} className="mx-auto mb-3 text-slate-400" />
              <p className="text-sm font-medium text-slate-600 mb-1">Upload Scanned Signed Copy</p>
              <p className="text-xs text-slate-400 mb-3">PDF, JPEG, PNG, or TIFF — Max 20MB</p>
              <label>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.tiff,.tif"
                  onChange={handleUploadSigned}
                  className="hidden"
                  data-testid="signed-copy-file-input"
                />
                <Button variant="default" className="gap-2 rounded-sm bg-cyan-600 hover:bg-cyan-700" asChild disabled={uploading}>
                  <span>
                    {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                    {uploading ? "Uploading..." : "Choose File & Upload"}
                  </span>
                </Button>
              </label>
            </div>
          </div>
        )}

        {/* Executed — allow downloading signed copy */}
        {isExecuted && (
          <div className="py-2">
            <Button
              variant="outline"
              className="gap-2 rounded-sm w-full"
              onClick={async () => {
                try {
                  const res = await apiClient.get(`/signatures/print-sign/${contract.id}/download-signed`, { responseType: "blob" });
                  const url = window.URL.createObjectURL(new Blob([res.data]));
                  const link = document.createElement("a");
                  link.href = url;
                  link.download = printSignStatus.signed_filename || "signed_contract";
                  link.click();
                  window.URL.revokeObjectURL(url);
                } catch { toast.error("Failed to download signed copy"); }
              }}
              data-testid="download-signed-copy-btn"
            >
              <Download size={16} /> Download Signed Copy
            </Button>
          </div>
        )}

        <DialogFooter>
          {step === "configure" && !isExecuted && (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
              <Button
                onClick={handleGeneratePdf}
                disabled={generating}
                className="rounded-sm gap-2 bg-cyan-600 hover:bg-cyan-700"
                data-testid="generate-signing-pdf-btn"
              >
                {generating ? <Loader2 size={16} className="animate-spin" /> : <Printer size={16} />}
                {generating ? "Generating..." : "Generate & Download PDF"}
              </Button>
            </>
          )}
          {(step === "upload" || isExecuted) && (
            <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Close</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StepIndicator({ number, label, active, done }) {
  return (
    <div className="flex items-center gap-2">
      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
        done ? "bg-emerald-100 text-emerald-700" :
        active ? "bg-cyan-100 text-cyan-700 ring-2 ring-cyan-300" :
        "bg-slate-100 text-slate-400"
      }`}>
        {done ? <CheckCircle size={14} /> : number}
      </div>
      <span className={`text-xs font-medium ${active ? "text-cyan-700" : done ? "text-emerald-600" : "text-slate-400"}`}>
        {label}
      </span>
    </div>
  );
}
